// Cross-screen data for the redesigned Marketing section. One fetch per
// remote resource per mount, shared by the dashboard and the Measure
// screens so tiles and detail views never disagree or double-fetch:
//
//   summary  — /api/data/marketing/summary   (soft $, CAC inputs, wonBySource)
//   ac       — /api/data/ac/summary          (ActiveCampaign audience)
//   gsc      — /api/gsc/status + /api/gsc/report (Search Console, 28d)
//   ga4Calls — /api/gsc/ga4/calls            (schedule_call conversions)
//   traffic  — /api/gsc/ga4/traffic?range=   (sessions, channels, landing pages)
//   funnel   — /api/gsc/ga4/funnel?range=    (signup + call conversion counts)
//   social   — /api/data/social/series + /schedule (series + woven posts)
//
// Also holds the top-bar date range (7d/30d/QTD). traffic + funnel
// refetch when the range changes; the other resources are range-free.

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch } from '../../utils/apiFetch';

const MarketingContext = createContext(null);

async function getJson(path) {
  const res = await apiFetch(path);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

const IDLE = { data: null, loading: true, error: null };

export function MarketingProvider({ children }) {
  const [range, setRange] = useState('30d'); // '7d' | '30d' | 'qtd'
  const [summary, setSummary] = useState(IDLE);
  const [ac, setAc] = useState(IDLE);
  const [gsc, setGsc] = useState(IDLE); // data = { status, report }
  const [ga4Calls, setGa4Calls] = useState(IDLE);
  const [traffic, setTraffic] = useState(IDLE);
  const [funnel, setFunnel] = useState(IDLE);
  const [social, setSocial] = useState(IDLE); // data = { series, posts }
  const [projects, setProjects] = useState(IDLE); // data = [{...project, steps}]
  const started = useRef(false);

  const loadSummary = useCallback(async () => {
    setSummary(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson('/api/data/marketing/summary');
      setSummary({ data, loading: false, error: null });
    } catch (e) {
      setSummary({ data: null, loading: false, error: e.message });
    }
  }, []);

  const loadAc = useCallback(async () => {
    setAc(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson('/api/data/ac/summary');
      setAc({ data, loading: false, error: null });
    } catch (e) {
      setAc({ data: null, loading: false, error: e.message });
    }
  }, []);

  const loadGsc = useCallback(async () => {
    setGsc(s => ({ ...s, loading: true, error: null }));
    try {
      const status = await getJson('/api/gsc/status');
      const report = status?.connected ? await getJson('/api/gsc/report') : null;
      setGsc({ data: { status, report }, loading: false, error: null });
    } catch (e) {
      setGsc({ data: null, loading: false, error: e.message });
    }
  }, []);

  const loadGa4Calls = useCallback(async () => {
    setGa4Calls(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson('/api/gsc/ga4/calls');
      setGa4Calls({ data, loading: false, error: null });
    } catch (e) {
      setGa4Calls({ data: null, loading: false, error: e.message });
    }
  }, []);

  const loadTraffic = useCallback(async (r) => {
    setTraffic(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson(`/api/gsc/ga4/traffic?range=${encodeURIComponent(r)}`);
      setTraffic({ data, loading: false, error: null });
    } catch (e) {
      setTraffic({ data: null, loading: false, error: e.message });
    }
  }, []);

  const loadFunnel = useCallback(async (r) => {
    setFunnel(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson(`/api/gsc/ga4/funnel?range=${encodeURIComponent(r)}`);
      setFunnel({ data, loading: false, error: null });
    } catch (e) {
      setFunnel({ data: null, loading: false, error: e.message });
    }
  }, []);

  const loadSocial = useCallback(async () => {
    setSocial(s => ({ ...s, loading: true, error: null }));
    try {
      const [seriesRes, schedRes] = await Promise.all([
        getJson('/api/data/social/series'),
        getJson('/api/data/social/schedule?weeks=3'),
      ]);
      setSocial({ data: { series: seriesRes.series || [], posts: schedRes.posts || [] }, loading: false, error: null });
    } catch (e) {
      setSocial({ data: null, loading: false, error: e.message });
    }
  }, []);

  const loadProjects = useCallback(async () => {
    setProjects(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson('/api/data/projects');
      setProjects({ data: data.projects || [], loading: false, error: null });
    } catch (e) {
      setProjects({ data: null, loading: false, error: e.message });
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    loadSummary();
    loadAc();
    loadGsc();
    loadGa4Calls();
    loadSocial();
    loadProjects();
  }, [loadSummary, loadAc, loadGsc, loadGa4Calls, loadSocial, loadProjects]);

  // Range-scoped resources refetch when the range changes (server caches
  // per range, so flipping back is instant).
  useEffect(() => {
    loadTraffic(range);
    loadFunnel(range);
  }, [range, loadTraffic, loadFunnel]);

  const value = {
    range, setRange,
    summary, ac, gsc, ga4Calls, traffic, funnel, social, projects,
    reloadSummary: loadSummary,
    reloadAc: loadAc,
    reloadGsc: loadGsc,
    reloadGa4Calls: loadGa4Calls,
    reloadSocial: loadSocial,
    reloadProjects: loadProjects,
  };

  return <MarketingContext.Provider value={value}>{children}</MarketingContext.Provider>;
}

export function useMarketing() {
  const ctx = useContext(MarketingContext);
  if (!ctx) throw new Error('useMarketing must be used inside MarketingProvider');
  return ctx;
}
